import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, map, switchMap, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TenantService } from 'src/app/tenant/services/tenant.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ITenantDTO } from 'src/app/shared/models/tenant';

@Injectable({ providedIn: 'root' })
/**
 * Guard that redirects users to their tenant's admin page if they are logged in.
 */
export class TenantRedirectGuard implements CanActivate {
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);

  canActivate(): Observable<boolean | UrlTree> {
    return this.authService.userData$.pipe(
      switchMap((userData) => {
        // If user is not logged in, allow access to home page
        if (!userData) {
          return of(true);
        }

        // Check if we already have an active tenant session
        const currentTenantCode = this.tenantService.tenantCode();
        if (currentTenantCode) {
          return of(this.router.createUrlTree([currentTenantCode, 'admin']));
        }

        // No active tenant session, redirect to first available tenant
        if (userData.tenants && userData.tenants.length > 0) {
          const firstTenantId = userData.tenants[0].id;

          return this.apiService
            .get<ITenantDTO>(`/tenants/${firstTenantId}`)
            .pipe(
              map((response) => {
                const tenant = response?.data;

                if (tenant?.shortCode) {
                  return this.router.createUrlTree([tenant.shortCode, 'admin']);
                }

                // If no shortCode found, allow access to home page
                return true;
              }),
            );
        }

        // Fallback: allow access to home page
        return of(true);
      }),
    );
  }
}
